import { memoize } from "lodash-es"

export const CONSTRAINTS = {
	MAX_FILE_SIZE: 30 * 1024 * 1024, // 30MB in bytes
	ALLOWED_MIME_TYPES: [
		"image/jpeg",
		"image/jpg",
		"image/png",
		"image/gif",
		"image/bmp",
		"image/webp",
	],
	ALLOWED_MIME_TYPES_WKN: ["JPEG/JPG", "PNG", "GIF", "BMP", "WebP"],
	MAX_SIZE_ERROR_MESSAGE: "File size exceeds maximum limit of 30MB",
	/** `FILE_UPLOAD.IMAGE_MAX_FILE_SIZE_BYTES` in the data-api. */
	IMAGE_MAX_FILE_SIZE: 10 * 1024 * 1024,
	IMAGE_MAX_SIZE_ERROR_MESSAGE:
		"The uploaded image exceeds the maximum allowed size of 10MB",
}

const downloadFile = (url: string, name?: string) => {
	try {
		new URL(url)
	} catch (_) {
		console.error("Invalid URL for download:", url)
		return
	}
	const a = document.createElement("a")
	a.style.display = "none"
	a.href = url
	a.target = "_blank"
	a.download = name || "download"
	const cleanup = () => {
		a.removeEventListener("click", cleanup)
		document.body.removeChild(a)
	}
	a.addEventListener("click", cleanup, { once: true })
	document.body.appendChild(a)
	a.click()
}

const fileSignatures: { [key: string]: string } = {
	"89504e47": "png",
	"424d": "bmp",
	"47494638": "gif",
	ffd8ff: "jpg",
	"25504446": "pdf",
	"52494646": "webp",
}

const getFileExtensionFromSignature = (header: string): string | null => {
	for (const signature in fileSignatures) {
		if (header.startsWith(signature)) {
			return fileSignatures[signature]
		}
	}
	return null
}

const getBlobFileExtension = async (blob: Blob): Promise<string | null> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader()
		reader.onloadend = (event: ProgressEvent<FileReader>) => {
			if (event.target?.readyState === FileReader.DONE) {
				const buffer = event.target.result as ArrayBuffer
				const uint8Array = new Uint8Array(buffer)
				const header = uint8Array
					.slice(0, 4)
					.reduce((acc, byte) => acc + byte.toString(16).padStart(2, "0"), "")
				resolve(getFileExtensionFromSignature(header))
			}
		}
		reader.onerror = () => {
			reject(new Error("Error reading blob."))
		}
		reader.readAsArrayBuffer(blob.slice(0, 4))
	})
}

export const downloadFileFromUrl = (
	url: string,
	name?: string,
): Promise<void> => {
	return new Promise((resolve, reject) => {
		fetch(url)
			.then(async (response) => {
				if (!response.ok) {
					throw new Error(`Network response was not ok: ${response.statusText}`)
				}
				const blob = await response.blob()
				const ext = await getBlobFileExtension(blob)
				const fileName =
					ext && name ? `${name}.${ext}` : name || `download.${ext}`
				const objectUrl = URL.createObjectURL(blob)
				downloadFile(objectUrl, fileName)
				setTimeout(() => URL.revokeObjectURL(objectUrl), 60 * 1000)
				resolve()
			})
			.catch((error) => {
				reject(error)
			})
	})
}

export const triggerDownload = async (blob: Blob, fileName?: string) => {
	const url = URL.createObjectURL(blob)
	const a = document.createElement("a")
	a.href = url
	a.target = "_blank"
	if (fileName) {
		a.download = fileName
	}
	document.body.appendChild(a)
	a.click()
	a.remove()
	setTimeout(() => URL.revokeObjectURL(url), 60 * 1000)
}

export const checkWebPSupport = memoize((): boolean => {
	const canvas = document.createElement("canvas")
	return canvas.getContext && canvas.getContext("2d")
		? canvas.toDataURL("image/webp").indexOf("data:image/webp") === 0
		: false
})

export const getBase64 = (file: Blob): Promise<string> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader()
		reader.readAsDataURL(file)
		reader.onload = () => resolve(reader.result as string)
		reader.onerror = (error) => reject(error)
	})

export async function dataUrlToFile(dataUrl: string, name?: string) {
	const response = await fetch(dataUrl)
	const blob = await response.blob()
	const ext = await getBlobFileExtension(blob)
	const fileName = ext && name ? `${name}.${ext}` : name || `download.${ext}`
	return new File([blob], fileName, { type: blob.type })
}

export function isBase64DataUrl(base64String: string) {
	// Regex to check for the data URL prefix and capture the actual Base64 part
	const matches = base64String.match(/^data:[a-zA-Z0-9\/+-]+;base64,(.*)$/)
	return matches && matches.length > 0
}

export function base64ToArrayBuffer(base64: string): ArrayBuffer {
	// Browser path
	const binaryString = atob(base64)
	const bytes = new Uint8Array(binaryString.length)
	for (let i = 0; i < binaryString.length; i++) {
		bytes[i] = binaryString.charCodeAt(i)
	}
	return bytes.buffer
}
